import { supabase } from '../lib/supabase'
import type { 
  BrandingProfile, 
  CreateBrandingProfileData,
  DocumentTemplate,
  FormalDocument,
  CreateFormalDocumentData
} from '../types/branding'

class BrandingService {
  // Branding Profile Methods
  
  async getBrandingProfile(userId: string): Promise<BrandingProfile | null> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { data, error } = await supabase
        .from('branding_profiles')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (error && error.code !== 'PGRST116') {
        throw error
      }

      if (!data) return null

      return {
        id: data.id,
        userId: data.user_id,
        businessName: data.business_name,
        tagline: data.tagline,
        businessAddress: data.business_address,
        businessPhone: data.business_phone,
        businessEmail: data.business_email,
        businessWebsite: data.business_website,
        vatNumber: data.vat_number,
        registrationNumber: data.registration_number,
        logoUrl: data.logo_url,
        logoPosition: data.logo_position,
        primaryColor: data.primary_color,
        secondaryColor: data.secondary_color,
        accentColor: data.accent_color,
        documentFont: data.document_font,
        headerStyle: data.header_style,
        footerText: data.footer_text,
        watermarkText: data.watermark_text,
        showWatermark: data.show_watermark,
        showBusinessAddress: data.show_business_address,
        showBusinessPhone: data.show_business_phone,
        showBusinessEmail: data.show_business_email,
        showBusinessWebsite: data.show_business_website,
        showVatNumber: data.show_vat_number,
        showRegistrationNumber: data.show_registration_number,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      }
    } catch (error) {
      console.error('Error fetching branding profile:', error)
      return null
    }
  }

  async createBrandingProfile(userId: string, data: CreateBrandingProfileData): Promise<{ success: boolean; profile?: BrandingProfile; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const profileData = {
        user_id: userId,
        business_name: data.businessName,
        tagline: data.tagline,
        business_address: data.businessAddress,
        business_phone: data.businessPhone,
        business_email: data.businessEmail,
        business_website: data.businessWebsite,
        vat_number: data.vatNumber,
        registration_number: data.registrationNumber,
        logo_position: data.logoPosition || 'left',
        primary_color: data.primaryColor || '#2563eb',
        secondary_color: data.secondaryColor || '#64748b',
        accent_color: data.accentColor || '#0ea5e9',
        document_font: data.documentFont || 'modern',
        header_style: data.headerStyle || 'professional',
        footer_text: data.footerText,
        watermark_text: data.watermarkText,
        show_watermark: data.showWatermark || false,
        show_business_address: data.showBusinessAddress ?? true,
        show_business_phone: data.showBusinessPhone ?? true,
        show_business_email: data.showBusinessEmail ?? true,
        show_business_website: data.showBusinessWebsite ?? true,
        show_vat_number: data.showVatNumber ?? false,
        show_registration_number: data.showRegistrationNumber ?? false
      }

      const { error } = await supabase
        .from('branding_profiles')
        .insert(profileData)
        .select()
        .single()

      if (error) throw error

      const brandingProfile = await this.getBrandingProfile(userId)
      
      return {
        success: true,
        profile: brandingProfile || undefined
      }
    } catch (error) {
      console.error('Error creating branding profile:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create branding profile'
      }
    }
  }

  async updateBrandingProfile(userId: string, updates: Partial<CreateBrandingProfileData>): Promise<{ success: boolean; profile?: BrandingProfile; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const updateData: any = {}
      
      // Map the updates to database column names
      if (updates.businessName !== undefined) updateData.business_name = updates.businessName
      if (updates.tagline !== undefined) updateData.tagline = updates.tagline
      if (updates.businessAddress !== undefined) updateData.business_address = updates.businessAddress
      if (updates.businessPhone !== undefined) updateData.business_phone = updates.businessPhone
      if (updates.businessEmail !== undefined) updateData.business_email = updates.businessEmail
      if (updates.businessWebsite !== undefined) updateData.business_website = updates.businessWebsite
      if (updates.vatNumber !== undefined) updateData.vat_number = updates.vatNumber
      if (updates.registrationNumber !== undefined) updateData.registration_number = updates.registrationNumber
      if (updates.logoPosition !== undefined) updateData.logo_position = updates.logoPosition
      if (updates.primaryColor !== undefined) updateData.primary_color = updates.primaryColor
      if (updates.secondaryColor !== undefined) updateData.secondary_color = updates.secondaryColor
      if (updates.accentColor !== undefined) updateData.accent_color = updates.accentColor
      if (updates.documentFont !== undefined) updateData.document_font = updates.documentFont
      if (updates.headerStyle !== undefined) updateData.header_style = updates.headerStyle
      if (updates.footerText !== undefined) updateData.footer_text = updates.footerText
      if (updates.watermarkText !== undefined) updateData.watermark_text = updates.watermarkText
      if (updates.showWatermark !== undefined) updateData.show_watermark = updates.showWatermark
      if (updates.showBusinessAddress !== undefined) updateData.show_business_address = updates.showBusinessAddress
      if (updates.showBusinessPhone !== undefined) updateData.show_business_phone = updates.showBusinessPhone
      if (updates.showBusinessEmail !== undefined) updateData.show_business_email = updates.showBusinessEmail
      if (updates.showBusinessWebsite !== undefined) updateData.show_business_website = updates.showBusinessWebsite
      if (updates.showVatNumber !== undefined) updateData.show_vat_number = updates.showVatNumber
      if (updates.showRegistrationNumber !== undefined) updateData.show_registration_number = updates.showRegistrationNumber

      const { error } = await supabase
        .from('branding_profiles')
        .update(updateData)
        .eq('user_id', userId)

      if (error) throw error

      const brandingProfile = await this.getBrandingProfile(userId)
      
      return {
        success: true,
        profile: brandingProfile || undefined
      }
    } catch (error) {
      console.error('Error updating branding profile:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update branding profile'
      }
    }
  }

  async uploadLogo(userId: string, file: File): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const fileExt = file.name.split('.').pop()
      const fileName = `${userId}/logo.${fileExt}`
      
      // Upload file to storage
      const { error: uploadError } = await supabase.storage
        .from('branding-assets')
        .upload(fileName, file, {
          upsert: true
        })

      if (uploadError) throw uploadError

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('branding-assets')
        .getPublicUrl(fileName)

      if (!urlData?.publicUrl) throw new Error('Failed to get public URL')

      // Update branding profile with logo URL
      const { error: updateError } = await supabase
        .from('branding_profiles')
        .update({ logo_url: urlData.publicUrl })
        .eq('user_id', userId)

      if (updateError) throw updateError

      return {
        success: true,
        url: urlData.publicUrl
      }
    } catch (error) {
      console.error('Error uploading logo:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload logo'
      }
    }
  }

  // Document Template Methods

  async getDocumentTemplates(userId: string, type?: DocumentTemplate['type']): Promise<DocumentTemplate[]> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      let query = supabase
        .from('document_templates')
        .select('*')
        .or(`user_id.eq.${userId},is_default.eq.true`)

      if (type) {
        query = query.eq('type', type)
      }

      query = query.order('is_default', { ascending: false })
        .order('created_at', { ascending: false })

      const { data, error } = await query

      if (error) throw error

      return (data || []).map(template => ({
        id: template.id,
        name: template.name,
        type: template.type,
        category: template.category,
        description: template.description,
        content: template.content,
        fields: template.fields || [],
        isDefault: template.is_default,
        userId: template.user_id,
        createdAt: template.created_at,
        updatedAt: template.updated_at
      }))
    } catch (error) {
      console.error('Error fetching document templates:', error)
      return []
    }
  }

  // Formal Document Methods

  async getFormalDocuments(userId: string, type?: FormalDocument['type']): Promise<FormalDocument[]> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      let query = supabase
        .from('formal_documents')
        .select(`
          *,
          client:clients(*),
          template:document_templates(*)
        `)
        .eq('user_id', userId)

      if (type) {
        query = query.eq('type', type)
      }

      query = query.order('created_at', { ascending: false })

      const { data, error } = await query

      if (error) throw error

      return (data || []).map(doc => ({
        id: doc.id,
        userId: doc.user_id,
        templateId: doc.template_id,
        clientId: doc.client_id,
        documentNumber: doc.document_number,
        title: doc.title,
        type: doc.type,
        status: doc.status,
        content: doc.content,
        fieldData: doc.field_data || {},
        issueDate: doc.issue_date,
        expiryDate: doc.expiry_date,
        signedDate: doc.signed_date,
        sentAt: doc.sent_at,
        viewedAt: doc.viewed_at,
        downloadedAt: doc.downloaded_at,
        client: doc.client ? {
          id: doc.client.id,
          name: doc.client.name,
          email: doc.client.email,
          company: doc.client.company
        } : undefined,
        template: doc.template ? {
          id: doc.template.id,
          name: doc.template.name,
          type: doc.template.type,
          category: doc.template.category,
          description: doc.template.description,
          content: doc.template.content,
          fields: doc.template.fields || [],
          isDefault: doc.template.is_default,
          userId: doc.template.user_id,
          createdAt: doc.template.created_at,
          updatedAt: doc.template.updated_at
        } : undefined,
        createdAt: doc.created_at,
        updatedAt: doc.updated_at
      }))
    } catch (error) {
      console.error('Error fetching formal documents:', error)
      return []
    }
  }

  async createFormalDocument(userId: string, data: CreateFormalDocumentData): Promise<{ success: boolean; document?: FormalDocument; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      // Generate document number
      const documentNumber = await this.generateDocumentNumber(userId, data.type)

      const documentData = {
        user_id: userId,
        template_id: data.templateId,
        client_id: data.clientId,
        document_number: documentNumber,
        title: data.title,
        type: data.type,
        field_data: data.fieldData,
        issue_date: new Date().toISOString().split('T')[0],
        expiry_date: data.expiryDate,
        content: '', // Will be filled by template processing
        status: 'draft'
      }

      const { data: document, error } = await supabase
        .from('formal_documents')
        .insert(documentData)
        .select()
        .single()

      if (error) throw error

      // Get the complete document with related data
      const documents = await this.getFormalDocuments(userId)
      const createdDocument = documents.find(d => d.id === document.id)

      return {
        success: true,
        document: createdDocument
      }
    } catch (error) {
      console.error('Error creating formal document:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create formal document'
      }
    }
  }

  private async generateDocumentNumber(userId: string, type: string): Promise<string> {
    try {
      const prefix = type.substring(0, 3).toUpperCase()
      const { data, error } = await supabase!
        .rpc('generate_document_number', { 
          user_uuid: userId,
          doc_type: type 
        })

      if (error) throw error
      return data || `${prefix}-${Date.now().toString().slice(-6)}`
    } catch (error) {
      console.error('Error generating document number:', error)
      // Fallback to timestamp-based number
      const prefix = type.substring(0, 3).toUpperCase()
      return `${prefix}-${Date.now().toString().slice(-6)}`
    }
  }

  async getDefaultBrandingProfile(userId: string): Promise<BrandingProfile> {
    // Get existing profile or create a default one
    const existing = await this.getBrandingProfile(userId)
    
    if (existing) {
      return existing
    }

    // Create a default branding profile
    const defaultProfile: CreateBrandingProfileData = {
      businessName: 'Your Photography Business',
      primaryColor: '#2563eb',
      secondaryColor: '#64748b',
      accentColor: '#0ea5e9',
      documentFont: 'modern',
      headerStyle: 'professional',
      logoPosition: 'left',
      showWatermark: false,
      showBusinessAddress: true,
      showBusinessPhone: true,
      showBusinessEmail: true,
      showBusinessWebsite: true,
      showVatNumber: false,
      showRegistrationNumber: false
    }

    const result = await this.createBrandingProfile(userId, defaultProfile)
    
    if (result.success && result.profile) {
      return result.profile
    }

    // Return a minimal default if creation fails
    return {
      id: '',
      userId,
      businessName: 'Your Photography Business',
      logoPosition: 'left',
      primaryColor: '#2563eb',
      secondaryColor: '#64748b',
      accentColor: '#0ea5e9',
      documentFont: 'modern',
      headerStyle: 'professional',
      showWatermark: false,
      showBusinessAddress: true,
      showBusinessPhone: true,
      showBusinessEmail: true,
      showBusinessWebsite: true,
      showVatNumber: false,
      showRegistrationNumber: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  }
}

export const brandingService = new BrandingService() 